import * as THREE from 'three';
import { SITE, MUSIC } from '../config.js';

// ---------------------------------------------------------------------------
// The 2D layer. Two rules:
//   1. Every link is reachable without a mouse — the rail is real navigation,
//      not decoration, so keyboard and screen-reader users get the same links.
//   2. Hovering a rail item and hovering the building itself are the same
//      event, so the page and the world never disagree about what is selected.
// ---------------------------------------------------------------------------

export class Hud {
    constructor(root, links, { onFocus, onOpen, onToggleClock, onScrub }) {
        this.root = root;
        this.links = links;
        this.onFocus = onFocus;
        this.onOpen = onOpen;
        this.active = null;

        this.el = {
            label: root.querySelector('#mill-label'),
            labelName: root.querySelector('#mill-label .label-name'),
            labelOpen: root.querySelector('#mill-label .label-open'),
            rail: root.querySelector('#rail'),
            clock: root.querySelector('#clock-time'),
            clockBtn: root.querySelector('#clock-toggle'),
            scrub: root.querySelector('#clock-scrub'),
            stats: root.querySelector('#stats'),
            tally: root.querySelector('#tally'),
            catch: root.querySelector('#catch'),
            hold: root.querySelector('#hold'),
            holdCount: root.querySelector('#hold .hold-count'),
            sheet: root.querySelector('#sheet'),
            sheetList: root.querySelector('#sheet-list'),
            sheetClose: root.querySelector('#sheet-close'),
            hint: root.querySelector('#fish-hint'),
            boatLabel: root.querySelector('#boat-label'),
            factCard: root.querySelector('#fact-card'),
            factText: root.querySelector('#fact-card .fact-text'),
            factTitle: root.querySelector('#fact-card .fact-title'),
            price: root.querySelector('#price'),
            priceSep: root.querySelector('#price-sep'),
            scrub: root.querySelector('#clock-scrub'),
            scrubLabel: root.querySelector('.scrub-label'),
        };
        this._boardKey = null;

        this._buildRail();

        // On a phone the fleet list will not fit in a corner, so the purse
        // collapses to your own row and opens the full list when tapped.
        this._rows = [];
        this.el.catch?.addEventListener('click', () => {
            if (this.root.body?.classList.contains('is-touch')) this.openSheet();
        });
        this.el.sheetClose?.addEventListener('click', () => this.closeSheet());
        this.el.sheet?.addEventListener('click', (e) => {
            if (e.target === this.el.sheet) this.closeSheet();   // tap the backdrop
        });

        this.el.clockBtn.addEventListener('click', () => onToggleClock());
        this.el.scrub.addEventListener('input', (e) => onScrub(parseFloat(e.target.value)));

        this._projected = new THREE.Vector3();
    }

    _buildRail() {
        this.railItems = new Map();
        for (const link of this.links) {
            const cfg = link.config;
            const item = document.createElement('a');
            item.className = 'rail-item';
            item.href = cfg.url;
            item.target = '_blank';
            item.rel = 'noopener noreferrer';
            item.dataset.id = cfg.id;
            item.style.setProperty('--accent', `#${cfg.accent.toString(16).padStart(6, '0')}`);
            item.innerHTML = `
                <span class="rail-swatch" aria-hidden="true"></span>
                <span class="rail-name">${cfg.name}</span>
            `;

            item.addEventListener('pointerenter', () => this.onFocus(cfg.id));
            item.addEventListener('pointerleave', () => this.onFocus(null));
            item.addEventListener('focus', () => this.onFocus(cfg.id));
            item.addEventListener('blur', () => this.onFocus(null));
            // A plain click flies the camera there first; the link still works
            // via middle-click, ctrl-click and the keyboard.
            item.addEventListener('click', (e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                this.onOpen(cfg.id, { fly: true });
            });

            this.el.rail.appendChild(item);
            this.railItems.set(cfg.id, item);
        }
    }

    /**
     * @param id      which building the world is talking about
     * @param sticky  true once the selection is committed by a click — the
     *                card then becomes a real link instead of a caption.
     */
    setActive(id, { sticky = false } = {}) {
        if (this.active === id && this.sticky === sticky) return;
        this.active = id;
        this.sticky = sticky;
        for (const [key, item] of this.railItems) {
            item.classList.toggle('is-active', key === id);
        }

        const link = this.links.find((l) => l.config.id === id);
        if (!link) {
            this.el.label.classList.remove('is-visible', 'is-sticky');
            this.el.label.removeAttribute('href');
            return;
        }
        this.el.labelName.textContent = link.config.name;
        this.el.label.style.setProperty(
            '--accent',
            `#${link.config.accent.toString(16).padStart(6, '0')}`,
        );
        // Without an href the card is not focusable and cannot be activated,
        // which is exactly what we want while it is only a hover caption.
        if (sticky) {
            this.el.label.href = link.config.url;
        } else {
            this.el.label.removeAttribute('href');
        }
        this.el.label.classList.toggle('is-sticky', sticky);
        this.el.label.classList.add('is-visible');
    }

    /** Pin the floating label above the active building, in screen space. */
    updateLabelPosition(camera) {
        if (!this.active) return;
        const link = this.links.find((l) => l.config.id === this.active);
        if (!link) return;

        this._projected.copy(link.worldAnchor).project(camera);
        const behind = this._projected.z > 1;
        const x = (this._projected.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-this._projected.y * 0.5 + 0.5) * window.innerHeight;
        this.el.label.style.transform = `translate(-50%, -100%) translate(${x}px, ${y}px)`;
        this.el.label.classList.toggle('is-offscreen', behind);
    }

    /**
     * The catch, as one card.
     *
     * Alone that is a single row with the herring on it. In company it is a
     * row per boat, flagged, because the pennant is the only thing telling one
     * tjalk from another out there — your own row is highlighted so you do not
     * have to count.
     *
     * Hidden entirely while every boat is still empty: an empty counter
     * announces a game to somebody who has not found a school yet, and finding
     * out there are fish out there is the better half of it.
     *
     * @param rows   [{ flag, count, you }] — one entry when sailing alone
     * @param landed true on the frame a fish comes aboard
     */
    setBoard(rows, { landed = false, people = 0, crates = 0 } = {}) {
        const el = this.el.catch;
        if (!el || !this.el.tally) return;

        const hold = rows?.find((r) => r.you)?.hold || 0;
        const anything = rows?.some((r) => r.count > 0 || r.hold > 0) || people > 0 || crates > 0;
        if (!rows?.length || !anything) {
            this.el.tally.hidden = true;
            this._boardKey = null;
            return;
        }
        this.el.tally.hidden = false;

        // The hold: fish, or people, never both — so the card shows whichever
        // is aboard, under the right icon.
        // Fish, people or cargo — one hold, so one number under whichever
        // icon matches what is actually in it.
        const carried = crates > 0 ? crates : people > 0 ? people : hold;
        if (this.el.holdCount) this.el.holdCount.textContent = String(carried);
        const art = this.el.hold?.querySelector('use');
        const want = crates > 0 ? '#crate-icon' : people > 0 ? '#people-icon' : '#herring-icon';
        if (art && art.getAttribute('href') !== want) {
            art.setAttribute('href', want);
            const box = { '#people-icon': '0 0 12 12', '#crate-icon': '0 0 12 12' }[want] || '0 0 16 12';
            art.parentNode.setAttribute('viewBox', box);
            art.parentNode.classList.toggle('is-people', want !== '#herring-icon');
        }

        // The purse: one row alone, one row per boat in company — except on a
        // phone, where the corner is not big enough for a list and the card
        // shows your own row with a count of the others beside it.
        this._rows = rows;
        const touch = this.root.body?.classList.contains('is-touch');
        const shown = touch ? rows.filter((r) => r.you) : rows;
        const others = rows.length - shown.length;
        const solo = shown.length === 1 && !others;
        el.classList.toggle('is-solo', solo);
        if (this.el.sheet && !this.el.sheet.hidden) this._fillSheet();

        const key = `${touch ? 't' : 'd'}:${others}:` + shown.map((r) => `${r.flag}:${r.count}:${r.you ? 1 : 0}`).join('|');
        if (key !== this._boardKey) {
            this._boardKey = key;
            el.innerHTML = shown.map((r) => {
                // Alone there is nobody to tell apart, so the coin takes the
                // place the flag would have had.
                const mark = solo
                    ? '<svg class="coin" aria-hidden="true"><use href="#coin-icon"/></svg>'
                    : `<span class="catch-flag" style="--flag:#${r.flag.toString(16).padStart(6, '0')}"></span>`;
                const more = touch && others > 0
                    ? `<span class="catch-more">+${others}</span>`
                    : '';
                return `<li class="catch-row${r.you ? ' is-you' : ''}">${mark}<span class="catch-count">${r.count}</span>${more}</li>`;
            }).join('');
        }

        if (landed && this.el.hold) {
            // Restart the animation rather than let it be swallowed while the
            // previous fish is still in the air.
            this.el.hold.classList.remove('is-landing');
            void this.el.hold.offsetWidth;
            this.el.hold.classList.add('is-landing');
        }
    }

    _fillSheet() {
        if (!this.el.sheetList) return;
        this.el.sheetList.innerHTML = this._rows.map((r) => `
            <li class="catch-row${r.you ? ' is-you' : ''}">
                <span class="catch-flag" style="--flag:#${r.flag.toString(16).padStart(6, '0')}"></span>
                <span class="catch-count">${r.count}</span>
            </li>
        `).join('');
    }

    openSheet() {
        if (!this.el.sheet || this._rows.length < 2) return;   // nothing to show
        this._fillSheet();
        this.el.sheet.hidden = false;
    }

    closeSheet() {
        if (this.el.sheet) this.el.sheet.hidden = true;
    }

    /** @param state 'ready' | 'fishing' | 'sell' | 'board' | 'land' | 'shut' | 'carrying' | 'buy' | 'unload' | 'millshut' | null */
    setHint(state, progress = 0, value = 0, extra = {}) {
        const el = this.el.hint;
        if (!el) return;
        if (!state) {
            el.hidden = true;
            el.classList.remove('is-fishing');
            return;
        }
        el.hidden = false;
        el.classList.toggle('is-fishing', state === 'fishing');
        // A phone has no space bar, so on touch the hint stops describing a
        // key and starts being the button.
        const touch = this.root.body?.classList.contains('is-touch');
        if (state === 'fishing') {
            const pct = Math.round(progress * 100);
            el.innerHTML = touch
                ? `stop hauling · ${pct}%`
                : `hauling — <kbd>space</kbd> to stop · ${pct}%`;
        } else if (state === 'carrying') {
            el.innerHTML = 'herring — but the hold is full of people';
        } else if (state === 'shut') {
            el.innerHTML = 'no crossing after dark';
        } else if (state === 'buy') {
            el.innerHTML = touch
                ? `take ${value} ${extra.name} · ƒ${extra.cost}`
                : `<kbd>space</kbd> for ${value} ${extra.name} · ƒ${extra.cost}`;
        } else if (state === 'unload') {
            el.innerHTML = touch
                ? `land the cargo · ƒ${value}`
                : `<kbd>space</kbd> to land the cargo · ƒ${value}`;
        } else if (state === 'millshut') {
            el.innerHTML = 'the mill is shut till morning';
        } else if (state === 'board') {
            el.innerHTML = touch
                ? `take ${value} aboard`
                : `<kbd>space</kbd> to take ${value} aboard`;
        } else if (state === 'land') {
            el.innerHTML = touch
                ? `put ${value} ashore`
                : `<kbd>space</kbd> to put ${value} ashore`;
        } else if (state === 'sell') {
            el.innerHTML = touch
                ? `sell the catch · ƒ${value}`
                : `<kbd>space</kbd> to sell the catch · ƒ${value}`;
        } else {
            el.innerHTML = touch ? 'fish here' : 'herring · <kbd>space</kbd> to fish';
        }
    }

    /** Another visitor's catch, pinned to their boat. Nothing at zero. */
    showBoatCatch(count, screen) {
        const el = this.el.boatLabel;
        if (!el) return;
        if (!count || !screen) {
            el.hidden = true;
            return;
        }
        el.hidden = false;
        el.textContent = `${count} herring`;
        el.style.transform = `translate(-50%, -140%) translate(${screen.x}px, ${screen.y}px)`;
    }

    /**
     * The card for a clicked school. Same idea as a building's card — click a
     * thing in the world, read about it — so it wears the same furniture.
     */
    showFact(title, text, screen, icon = 'herring-icon') {
        const el = this.el.factCard;
        if (!el) return;
        if (this.el.factTitle && title) {
            const label = this.el.factTitle.querySelector('.fact-label');
            if (label) label.textContent = title;
            const art = this.el.factTitle.querySelector('.fact-fish use');
            if (art && art.getAttribute('href') !== `#${icon}`) {
                art.setAttribute('href', `#${icon}`);
                // The tower is taller than it is wide; the fish is the reverse.
                art.parentNode.classList.toggle('is-tall', icon !== 'herring-icon');
                const box = {
                    'herring-icon': '0 0 16 12',
                    'people-icon': '0 0 12 12',
                }[icon] || '0 0 12 16';
                art.parentNode.setAttribute('viewBox', box);
            }
        }
        if (!text) {
            el.classList.remove('is-visible');
            el.hidden = true;
            return;
        }
        el.hidden = false;
        this.el.factText.textContent = text;
        el.classList.add('is-visible');
        if (screen) {
            el.style.transform = `translate(-50%, -118%) translate(${screen.x}px, ${screen.y}px)`;
        }
    }

    /** What herring is fetching at the market, to one decimal. */
    setPrice(price) {
        const el = this.el.price;
        if (!el) return;
        const text = `ƒ${price.toFixed(1)} a herring`;
        if (text === this._priceText) return;
        this._priceText = text;
        el.hidden = false;
        if (this.el.priceSep) this.el.priceSep.hidden = false;
        // No coin here: the florin sign already says what the number is, and
        // two currency marks on one price is one too many.
        el.textContent = text;
    }

    /**
     * The scrub and the pause button are development tools: handy for checking
     * dusk, clutter for a visitor who only wants to sail.
     *
     * Pausing is worse than clutter now that the sky runs on the relay's
     * clock — a paused visitor is the one person sailing under a different sun
     * to everybody else. The clock face itself stays; reading the time is
     * harmless.
     */
    hideClockTools() {
        if (this.el.scrub) this.el.scrub.hidden = true;
        if (this.el.scrubLabel) this.el.scrubLabel.hidden = true;
        if (this.el.clockBtn) this.el.clockBtn.hidden = true;
    }

    setClock(text, paused) {
        this.el.clock.textContent = text;
        this.el.clockBtn.setAttribute('aria-pressed', String(paused));
        this.el.clockBtn.textContent = paused ? '▶' : '❚❚';
        this.el.clockBtn.title = paused ? 'Resume the day' : 'Freeze the light';
    }

    /**
     * Engine stats are for us, not for visitors — pass null to hide them and
     * the separator with them, leaving just the interaction hint.
     */
    setStats(text) {
        if (!this.el.stats) return;
        const sep = this.root.querySelector('.colophon-sep');
        this.el.stats.textContent = text || '';
        if (sep) sep.hidden = !text;
    }
}

/** True when served from a dev machine rather than the real domain. */
export function isDevHost() {
    const { hostname } = window.location;
    return hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname === '[::1]'
        || hostname === ''
        || hostname.endsWith('.local');
}

/**
 * Show who wrote the music. CC-BY means the credit is a condition of use, not
 * a courtesy, so it appears whenever a track is configured — not only while
 * something is playing.
 */
export function applyMusicCredit(root) {
    const link = root.querySelector('#music-credit');
    const sep = root.querySelector('#music-credit-sep');
    if (!link || !MUSIC.credit || (!MUSIC.day && !MUSIC.night)) return;
    link.textContent = MUSIC.credit;
    link.href = MUSIC.creditUrl || '#';
    link.hidden = false;
    if (sep) sep.hidden = false;
}

/** Fill in the static copy so config.js stays the single source of truth. */
export function applySiteCopy(root) {
    const name = root.querySelector('#site-name');
    const tagline = root.querySelector('#site-tagline');
    if (name) name.textContent = SITE.name;
    if (tagline) tagline.textContent = SITE.tagline;
    // index.html already carries the real title; overwriting it here would
    // throw away the slogan the moment the bundle loads.
}
